/**
 * Canonical shape of an internal link target — ONE definition (rule 64).
 *
 * The static-SEO worker rewrites internal hrefs at serve time, so a stored
 * artifact and the page a crawler actually receives can disagree. Rule 64's
 * link-population gate compares an exported artifact's links against the final
 * artifact population; if the gate reads the RAW href while the serving layer
 * rewrites it, a link that resolves perfectly in production is reported as
 * absent and the whole publish aborts (2026-07-26 incident).
 *
 * Both sides therefore share this function: `html-enrichment.ts` applies it to
 * rewrite served hrefs, and `scripts/export-static-seo.mjs` applies it to the
 * link targets it checks. Never hand-copy either rule into a consumer — that is
 * the drifting-copies class (rules 23/24).
 *
 * The production exporter runs under bare Node, so the one shared helper import
 * carries an explicit extension (rule 64). Taking the legacy map as an argument
 * keeps every other canonical rewrite dependency-free.
 */

import {
  canonicalCompatibilityPair,
  canonicalCompatibilityPath,
} from "../../shared/compatibility-path.ts";
import { withTrailingSlash } from "../../shared/canonical-url.ts";
import crawlPolicyData from "../../src/data/crawl-policy-data.json" with { type: "json" };

export { canonicalCompatibilityPair, canonicalCompatibilityPath };

/**
 * Nakshatra ids are underscore-separated internally (`purva_ashadha`) while the
 * published route is hyphenated (`/nakshatra/purva-ashadha/`). Only the six
 * two-word nakshatras carry an underscore, so single-word ids are untouched.
 */
const NAKSHATRA_UNDERSCORE_ID_RE = /^\/nakshatra\/([^/?#]*_[^/?#]*)(\/.*)?$/;

/**
 * A rashi-pair compatibility URL is canonical only in SORTED order, so both
 * orderings (`/compatibility/meen-vrishchik/` and `/compatibility/vrishchik-meen/`)
 * consolidate onto one indexable URL instead of competing as self-canonical
 * duplicates. Ordered variants stay routable and canonicalize here.
 *
 * This lived only in `src/app/compatibility/[pair]/_metadata.ts` as a bare
 * `.sort()`, so the static exporter — which requires `canonical === self` — read
 * the governed cross-canonical as drift and quarantined all 40 ordered variants
 * seeded by the rule-76 registry. Sharing the rule is what makes the serve-time
 * rewrite, the page's own canonical tag, and the export-time check agree.
 *
 * Deliberately shape-based, not rashi-id-validated: exactly one hyphen between
 * two alpha runs is the pair shape. An unknown pair sorts harmlessly and still
 * resolves to Not Found upstream.
 */
const COMPATIBILITY_PAIR_RE = /^(\/(?:hi\/)?compatibility)\/([a-z]+)-([a-z]+)(\/?)$/;

const LEGACY_PATTERNS = crawlPolicyData.legacyCrawlCanonicalPathPatterns.map((entry) => ({
  pattern: new RegExp(entry.pattern),
  target: entry.target,
}));

/** Exact redirects take precedence over governed pattern-shaped retirements. */
export function legacyCanonicalPath(
  path: string,
  legacyCanonicalPaths: Readonly<Record<string, string>>
): string | null {
  const normalized = withTrailingSlash(path);
  const exact = legacyCanonicalPaths[path] ?? legacyCanonicalPaths[normalized];
  if (exact) return exact;
  for (const { pattern, target } of LEGACY_PATTERNS) {
    const match = pattern.exec(normalized);
    if (match) {
      const resolved = target.replace(/\$(\d)/g, (_, index) => match[Number(index)] ?? "");
      // Exact-map targets are already checked to be terminal destinations.
      // A retired year must reach that destination without a second redirect.
      return legacyCanonicalPaths[resolved] ?? resolved;
    }
  }
  return null;
}

/**
 * Return the canonical path a crawler/user is actually served for `path`.
 * Query/fragment must be split off by the caller; this operates on the path
 * alone and returns `path` unchanged when no rewrite applies.
 */
export function canonicalizeInternalPath(
  path: string,
  legacyCanonicalPaths: Readonly<Record<string, string>>
): string {
  if (!path.startsWith("/")) return path;

  const legacy = legacyCanonicalPath(path, legacyCanonicalPaths);
  if (legacy) return legacy;

  const nakshatra = NAKSHATRA_UNDERSCORE_ID_RE.exec(path);
  if (nakshatra) {
    return `/nakshatra/${nakshatra[1].replace(/_/g, "-")}${nakshatra[2] ?? "/"}`;
  }

  const compatibility = COMPATIBILITY_PAIR_RE.exec(path);
  if (compatibility) {
    const [prefix, a, b, trailing] = compatibility.slice(1);
    return `${prefix}/${canonicalCompatibilityPair(a, b)}${trailing}`;
  }

  return path;
}
